/** @module csi/heading/Ref */
define([
	"dojo/_base/declare",
	"csi",
	"csi/heading/_base",
	"csi/heading/AggregateAttribute",
	"csi/heading/Attribute",
	"csi/heading/RendererStoreMixin",
	"csi/html",
	"csi/locale",
	"csi/middleware/common",
	"csi/middleware/customView/customViewDataManagementService",
	"csi/middleware/inline/InlinePopupService",
	"csi/store/DelayedRequest",
	"csi/widget/InlineSelect",
	"csi/xml",

	"./RefDelete",
	"./RefEdit"
], function(declare, csi, csiHeading, AggregateAttribute, Attribute, RendererStoreMixin, html, locale,
	{convertNamesStoreDataToComponentItems}, {lessThanGreaterThanOps}, {CONTROL_TYPE_SINGLE_SELECT}, {DelayedRequest}, InlineSelect, xml,) {

	return declare([Attribute, RendererStoreMixin], {
		defaultValue: csi.EMPTY_URL,
		queryOps: csiHeading.QUERY_OPS[xml.ATTR_REF],
		type: xml.ATTR_REF,

		/** @property {boolean} bQueryByRef - set to true to query by ref value rather than referenced node's name */
		//bQueryByRef: false,

		canLink: function() {
			// summary: ref attributes are already links
			return false;
		},

		canCopy: function(/*Context*/ctxCopy, /*Heading*/h) {
			// summary: only allow copying of ref if there is a key to the store so we can verify
			// if pasting to a cell with the same store
			if (this.inherited(arguments)) {
				var key = this.getStoreKey(ctxCopy, h);
				if (key) {
					ctxCopy.__copyKey = key;
					return true;
				}
			}

			return false;
		},

		canPaste: function(/*Context*/ctxCopy, /*Context*/ctxPaste, /*Heading*/h) {
			// summary: check store key against copied node's store key
			var key = this.getStoreKey(ctxPaste, h);
			return this.inherited(arguments) && (key == ctxCopy.__copyKey);
		},

		/**
		 * @override
		 * If value is ref rather than normal name (of the referenced node), then try looking up name for node
		 */
		formatQueryFilterValue: function(ctx, value, format, bFloatingDates, bSelected) {
			if (ctx && this.bQueryByRef) {
				const node = ctx.cache.get(value);
				if (node) {
					return this.getNodeDisplayName(ctx, node);
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * For filtering, nodes with empty names should return a blank value rather than (Unnamed)
		 */
		formatValueForFiltering: function(ctx, h, value) {
			return value;
		},

		/**
		 * @override
		 * If filtering by ref, then need to sort by formatted value (if provided) rather than URL
		 */
		formatValueForSorting: function(value, format, formattedValue) {
			return (this.bQueryByRef && formattedValue) ? formattedValue : value;
		},

		fromHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/editNode) {
			// summary: return attribute value from edit control this is to support the MultiSelect widget in form
			return editNode.get("externalValue") || editNode.get("value");
		},

		getNodeDisplayName: function(/*Context*/ctx, /*Node*/node) {
			// summary: return name of referenced node
			return locale.getNodeReferenceName(node, ctx.cache, ctx.locale);
		},

		/**
		 * @override
		 * For ref attributes, the default is to filter by referenced name rather than the ref value itself
		 */
		getQuerySpec: function(ctx, h, spec) {
			// if just a ref attribute (no relation), then do not allow querying by it
			var id = h.m_id;
			var index = (spec.index == null) ? h.m_index : spec.index;
			var path = spec.getQueryPath();
			var type = xml.ATTR_STRING;
			var ren = csiHeading.getRenderer(type);
			var rcDest = spec.rcDest;
			var blanksOp;
			var prop;

			if (this.bQueryByRef) {
				id = null;
				index = null;
				prop = xml.PROP_URL;
				ren = this;
			} else if (rcDest && rcDest.isLocalizedItem) {
				// determine if class is LocalizedItem which will affect how heading is sorted/filtered
				blanksOp = xml.OP_BLANK_LOCALIZED;
				id = xml.FILTER_LOCALIZED_NAME;
				index = ctx.locale || locale.getLocale();
			} else {
				id = xml.ATTR_NAME;
				index = null;
			}

			return {
				allowFilteringBySelectionOfExistingValues: true,
				blanksOperator: blanksOp,
				id: id,
				index: index,
				path: path,
				prop: prop,
				ops: csiHeading.allowTextComparisonOperators ? this.queryOps.concat(lessThanGreaterThanOps) : this.queryOps,
				relative: true,
				ren: ren,
				type: type
			};
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: render value for attribute in HTML
			var node = this._getReferencedNode(ctx, h);
			if (!node) {
				return false;
			}

			var nodeLink = this._getLinkNode(ctx, h, node);
			var anchor = this._toHtmlAnchor(ctx, h, refNode, nodeLink);
			html.text(anchor || refNode, this.getNodeDisplayName(ctx, node));
			return true;
		},

		toHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Object*/params) {
			// summary: create store for enumeration values and select control to edit
			if (params.value == null) {
				params.value = this._getValue(ctx, h) || this.getDefaultValue(h);
			}

			if (params.store == null) {
				params.store = this.getStore(ctx, h, params.value);
			}

			return new InlineSelect(params);
		},

		toValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return name of referenced node
			var node = this._getReferencedNode(ctx, h);
			return node ? this.getNodeDisplayName(ctx, node) : "";
		},

		_getReferencedNode: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return referenced node
			var url = this._getValue(ctx, h);
			var node = ctx.cache.getNode(url, !ctx.bPathsQueried);

			if (!node) {
				ctx.testForUnresolvedUrl(url);
			}

			return node;
		},

		/**
		 * @override
		 * Prevent action if the URL is valid, but user does not have access to node
		 */
		_getRenderAction(ctx, h) {
			const url = this._getValue(ctx, h);

			if (xml.isValidURL(url) && !this._getReferencedNode(ctx, h)) {
				return null;
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Return aggregate action if not configured with Heading
		 */
		_getRenderActionForHeading(ctx, h) {
			const a = new AggregateAttribute();
			a.aggregateId = h.m_id;
			return a;
		},

		_toFilterValue: function(/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: return value to be used in query when sorting/filtering by heading
			return locale.getNodeStoredName(ctx.cache.getNode(value), ctx.cache, ctx.locale) || "";
		},

		/** React */

		/**
		 * @override
		 */
		getReactControlOptions: async function(store, controller, query, queryOptions) {
			const data = await store.query(query, queryOptions);
			const options = convertNamesStoreDataToComponentItems(data, store);

			// at this moment aggregateController._onAggregateStoreResponse() is already completed, so we can use refreshed contoller.oldValue
			const {_actionViewParameters, oldValue} = controller;
			// if value is "required" then empty value will be removed from dropdown and therefore dropdown value can't be initialized to empty url, 
			// in this case it must be null or assertion will happen and shown in console
			let value;
			if (controller.isValueRequired() && xml.isEmptyURL(oldValue)) {
				value = null;
			} else {
				value = options.find(({id}) => id === oldValue);
			}

			const selectActions = controller.getSelectActions(_actionViewParameters, store);
			return {
				options,
				selectActions,
				store,
				value,
			};
		},

		/**
		 * @override
		 */
		getReactPlaceholderOptions: function(controller, ctx, h) {
			const {delaySearch, oldValue} = controller;
			const store = this.getStore(ctx, h, oldValue);
			const selectActions = controller.getSelectActions(controller._actionViewParameters, store);
			let delayedStore;
			if (delaySearch) {
				delayedStore = new DelayedRequest(store);
			}
			const effectiveStore = delaySearch ? delayedStore : store;

			if (!oldValue || xml.isEmptyURL(oldValue)) {
				return {
					options: [],
					selectActions,
					store: effectiveStore,
					value: null,
				};
			}
			const data = [store.getFromMemory(oldValue)];
			const options = convertNamesStoreDataToComponentItems(data, store);
			const value = options.find(({id}) => id === oldValue);
			return {
				options,
				selectActions,
				store: effectiveStore,
				value,
			};
		},

		/**
		 * @override
		 * @param {context} ctx
		 * @return {string}
		 */
		getReactControlType: function(ctx) {
			return CONTROL_TYPE_SINGLE_SELECT;
		},

		/** 
		 * return true if react renderer exists for given attribute type and should be used
		 */
		useReactInlineDialog() {
			return true;
		},

	});
});